// Add the tune you are LOOKING AT to a playlist (Aug 2026).
//
// Until now the only way in was the Tunes browser: to file the tune already on
// screen you had to open a list of everything and find it again. Every other
// app with playlists puts this on the tune itself, and so does this.
//
// It mirrors the browser's Add-to menu deliberately — same shape, same inline
// "New playlist…" — so the two places you can do this do not feel like two
// different features.

import { useMemo, useState, type ReactNode } from 'react';
import { ListPlus, Plus, X } from 'lucide-react';
import { Popover } from '@/components/Popover';
import { useStore, type PlaylistTuneRef } from '@/state/store';
import { playlistsAvailable, usePlaylists, usePlaylistsAvailable } from '@/data/playlistAccess';
import { canEditPlaylist } from '@/storage/playlistLinks';
import { playlistRefKey } from '@/storage/playlists';
import { showPlaylistAdded } from './playlistAdded';

export type AddTuneToPlaylistControls = {
  renderPanel: (close: () => void) => ReactNode;
  title: string;
};

export type AddTuneToPlaylistProps = {
  compact?: boolean;
  /** Add an unopened search result without replacing or saving the current tune. */
  tuneRef?: PlaylistTuneRef;
  /** The tune's display title; defaults to the supplied reference or current score. */
  title?: string;
  /** Controls are undefined while playlists are not available (signed out). */
  children?: (controls: AddTuneToPlaylistControls | undefined) => ReactNode;
};

/**
 * The menu itself, also the home cards' dialog (TileAddToPlaylist). Its
 * naming draft and the armed "add again?" live HERE, so they end when the
 * menu closes: the armed item's blur never fires on Safari (a tapped button
 * takes no focus), and an arm that outlived the menu turned the next single
 * tap, on this tune or the next, into a duplicate.
 */
export function AddTuneToPlaylistPanel({
  close,
  onAdded,
  tuneRef,
}: {
  close: () => void;
  /** After the add, which the app's notice (playlistAdded.ts) already says. */
  onAdded?: (listName: string, playlistId: string, entryId: string | null) => void;
  tuneRef?: PlaylistTuneRef;
}) {
  // A playlist shared with you view only is not yours to add to.
  const allPlaylists = usePlaylists();
  const playlistLinks = useStore((s) => s.playlistLinks);
  const lastAddedTo = useStore((s) => s.lastPlaylistAddedTo);
  const userPlaylists = useMemo(() => {
    const editable = allPlaylists.filter((p) => canEditPlaylist(playlistLinks, p.id));
    // The list you last filed into first (Oct 2026): filing is a run of adds
    // into one list, and in creation order that list was wherever it fell.
    const recent = editable.find((p) => p.id === lastAddedTo);
    return recent ? [recent, ...editable.filter((p) => p !== recent)] : editable;
  }, [allPlaylists, playlistLinks, lastAddedTo]);
  const currentTuneRef = useStore((s) => s.currentTuneRef);
  const ref = tuneRef ?? currentTuneRef;

  const [naming, setNaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** A list this tune is already in, armed for a deliberate second copy. */
  const [confirmFor, setConfirmFor] = useState<string | null>(null);

  const reset = () => {
    setNaming(false);
    setDraft('');
    setConfirmFor(null);
    setError(null);
  };

  const add = (playlistId: string) => {
    // A shared list may have been removed or made read only while its menu
    // was open. Check the current state before claiming the add succeeded.
    if (!playlistsAvailable()) {
      setError('Sign in to add tunes to a playlist.');
      return;
    }
    const state = useStore.getState();
    const list = state.userPlaylists.find((p) => p.id === playlistId);
    if (!list) {
      setError('This playlist is no longer available.');
      return;
    }
    if (!canEditPlaylist(state.playlistLinks, playlistId)) {
      setError('This playlist is read only.');
      return;
    }
    const currentRef = tuneRef ?? state.currentTuneRef;
    const already =
      !!currentRef &&
      list.entries.some((e) => playlistRefKey(e.ref) === playlistRefKey(currentRef));
    if (already && confirmFor !== playlistId) {
      setError(null);
      setConfirmFor(playlistId);
      return;
    }
    const entryId = tuneRef
      ? state.addToPlaylist(playlistId, tuneRef)
      : state.addCurrentTuneToPlaylist(playlistId).entryId;
    if (!entryId) {
      setError('Could not add this tune to the playlist.');
      return;
    }
    // One notice for every way of filing, with Open and Undo.
    showPlaylistAdded({ playlistId, playlistName: list.name, entryId });
    onAdded?.(list.name, playlistId, entryId);
    reset();
    close();
  };

  const createAndAdd = () => {
    const name = draft.trim();
    if (!name) return;
    if (!playlistsAvailable()) {
      setError('Sign in to add tunes to a playlist.');
      return;
    }
    add(useStore.getState().createPlaylist(name));
  };

  return (
    <div className="add-menu-panel" role="menu">
      {/* Said plainly, because it is a real difference: with no live
          reference the tune is SAVED into the library first, and that
          is a thing the user now owns a copy of. */}
      {!ref && (
        <p className="add-menu-note muted" data-testid="add-menu-will-save">
          Not in your library yet — adding it will save a copy first.
        </p>
      )}

      {/* New playlist FIRST, as iReal Pro has it: the reason you opened
          this menu and found nothing to click is usually that the list
          you want does not exist yet. */}
      {naming ? (
        <div className="add-menu-naming">
          <input
            className="text-input"
            data-testid="add-menu-name"
            aria-label="New playlist name"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Adding closes the dialog and hands focus back to the button
              // that opened it; an Enter left to run on would press that
              // button and open the dialog again.
              if (e.key === 'Enter') {
                e.preventDefault();
                createAndAdd();
              }
              if (e.key === 'Escape') reset();
            }}
          />
          <button
            className="btn btn-sm btn-primary"
            data-testid="add-menu-create"
            onClick={createAndAdd}
          >
            Create
          </button>
          <button className="btn btn-sm btn-ghost" aria-label="Cancel new playlist" onClick={reset}>
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      ) : (
        <button
          className="add-menu-item add-menu-new"
          data-testid="add-menu-new"
          onClick={() => {
            setConfirmFor(null);
            setError(null);
            setNaming(true);
          }}
        >
          <Plus size={14} aria-hidden="true" /> New playlist…
        </button>
      )}

      {userPlaylists.length === 0 && !naming && (
        <p className="add-menu-note muted">No playlists yet.</p>
      )}

      {userPlaylists.map((p) => {
        const already =
          !!ref && p.entries.some((e) => playlistRefKey(e.ref) === playlistRefKey(ref));
        const arming = confirmFor === p.id;
        return (
          <button
            key={p.id}
            className={`add-menu-item${already ? ' in-list' : ''}${arming ? ' is-arming' : ''}`}
            data-testid="add-menu-playlist"
            // Already in the list: ask rather than silently duplicating.
            // A set CAN hold a tune twice (an opener reprised as the
            // encore) so refusing would be wrong — but that is a
            // deliberate act, not something a stray tap should do.
            onClick={() => add(p.id)}
            onBlur={() => arming && setConfirmFor(null)}
          >
            {p.name}
            {p.id === lastAddedTo && userPlaylists.length > 1 && (
              <span className="add-menu-recent" data-testid="add-menu-recent">
                last used
              </span>
            )}
            {arming ? (
              <span className="add-menu-again" data-testid="add-menu-again">
                Already in — add again?
              </span>
            ) : (
              <>
                <span className="muted">{p.entries.length}</span>
                {already && (
                  <span className="add-menu-check" aria-label="already in this playlist">
                    ✓
                  </span>
                )}
              </>
            )}
          </button>
        );
      })}
      {error && (
        <p className="import-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Standalone picker, or a mounted controller for a parent menu via children. */
export function AddTuneToPlaylist({
  compact = false,
  tuneRef,
  title: tuneTitle,
  children,
}: AddTuneToPlaylistProps = {}) {
  const available = usePlaylistsAvailable();
  const currentTuneRef = useStore((s) => s.currentTuneRef);
  const scoreTitle = useStore((s) => s.score.title);

  const ref = tuneRef ?? currentTuneRef;
  const displayTitle = tuneTitle ?? tuneRef?.title ?? scoreTitle;
  const blank = displayTitle.trim() === '' || displayTitle === 'Untitled Chart';
  const title = blank ? 'Add this chart to a playlist' : `Add ${displayTitle} to a playlist`;

  // A fresh panel for every opening, and keyed by the tune so an arm never
  // passes to the next one loaded. The add's notice is the app's one
  // (PlaylistAddedToast), not this component's.
  const renderPanel = (close: () => void) => (
    <AddTuneToPlaylistPanel
      key={ref ? playlistRefKey(ref) : 'unreferenced'}
      close={close}
      tuneRef={tuneRef}
    />
  );

  if (children) return children(available ? { renderPanel, title } : undefined);
  if (!available) return null;

  return (
    <Popover
      label={
        <span className="tune-action-label">
          <ListPlus size={compact ? 18 : 16} strokeWidth={1.8} aria-hidden="true" />
          {!compact && <span>Add to</span>}
        </span>
      }
      title={title}
      testid="tune-add-to-playlist"
      align="right"
    >
      {renderPanel}
    </Popover>
  );
}
